import React, { useContext, useEffect, useRef, useState } from "react";
import cx from "classnames";
import styles from "../../scss/AFDialog.module.scss";
import LModal from "../../../common/components/Modals/LModal/LModal";
import Button from "../../../common/components/Forms/Button/Button";
import { Form, Formik } from "formik";
import Dropdown from "../../../common/components/Forms/Dropdown/Dropdown";
import { titleOptionList } from "../../constants/SetupData";
import Input from "../../../common/components/Forms/Input/Input";
import { TextField } from "@material-ui/core";
import Checkbox from "../../../common/components/Forms/Checkbox/Checkbox";
import AFAddressField from "../AFAddressField";
import { IAddress } from "../../interfaces/IAppFormData";
import * as Yup from "yup";
import { addPolicyOwner } from "../../api/ApiAppForm";
import { AppFormControllerContext } from "../../providers/AppFormProvider";
import useAFForm from "../../hooks/useAFForm";
import ValidationMessage from "../../../common/components/Forms/ValidationMessage/ValidationMessage";

interface IAFAddPolicyOwnerDialog {
    isOpen: boolean;
    applicationId: string;
    onClose: (isContinue: boolean) => void;
}

const AFAddPolicyOwnerDialog: React.FC<IAFAddPolicyOwnerDialog> = (props) => {
    const { isOpen, onClose } = props;

    const [isSameWithPostalAddress, setIsSameWithPostalAddress] = useState(true);
    const [homeAddress, setHomeAddress] = useState<IAddress>();
    const [postalAddress, setPostalAddress] = useState<IAddress>();
    const [isLoading, setIsLoading] = useState(false);
    const [showAddress, setShowAddress] = useState(false);
    const [googlePlacesApiKey, setGooglePlacesApiKey] = useState("");
    const { selectors } = useContext(AppFormControllerContext);
    const [isErrorDOB, setIsErrorDOB] = useState("");

    const { validDOB } = useAFForm();

    const initialValue = {
        title: "",
        firstName: "",
        middleName: "",
        lastName: "",
        gender: "",
        dateOfBirth: "",
        mobile: "",
        home: "",
        email: ""
    };

    const validationSchema = Yup.object().shape({
        title: Yup.string().trim().required("Please select title"),
        firstName: Yup.string().trim().required("This field is required"),
        lastName: Yup.string().trim().required("This field is required"),
        gender: Yup.string().trim().required("This field is required"),
        dateOfBirth: Yup.string().trim().required("This field is required"),
        mobile: Yup.string().required("This field is required"),
        home: Yup.string().required("This field is required"),
        email: Yup.string()
            .trim()
            .email("Invalid email")
            .required("This field is required")
    });

    const handleAddress = (answerInfo) => {
        var answerJson = answerInfo?.answer;
        if (answerJson && answerJson !== "") {
            setHomeAddress(JSON.parse(answerInfo?.answer));
        }
        else {
            setHomeAddress(null);
        }
    };

    const handlePostalAddress = (answerInfo) => {
        setPostalAddress(JSON.parse(answerInfo?.answer));
    };

    const handleSubmitForm = (values): void => {
        setIsLoading(true);
        const addresses = [
            {
                isSameWithPostalAddress: isSameWithPostalAddress,
                addressType: "Home",
                address: homeAddress
            }
        ];
        if (!isSameWithPostalAddress) {
            addresses.push({
                isSameWithPostalAddress: false,
                addressType: "Postal",
                address: postalAddress
            });
        }
        const params = {
            ...values,
            addresses
        };
        addPolicyOwner(parseInt(props.applicationId), selectors.getQuoteGroupId(), params)
            .finally(() => {
                setIsLoading(false);
                onClose(true);
            });
    };

    useEffect(() => {
        setShowAddress(Boolean(window.googlePlacesApiKey));
        if (Boolean(window.googlePlacesApiKey))
            setGooglePlacesApiKey(window.googlePlacesApiKey);
    }, [window.googlePlacesApiKey]);

    return (
        <LModal
            Header={() => null}
            calcHeightMode="auto"
            width="wide"
            removePadding={true}
            isOpen={isOpen}
            onClose={() => {
                setIsErrorDOB("");
                onClose(false);
            }}>
            {() => {
                return (
                    <div
                        className={cx(
                            styles["af-warning-dialog-modal"],
                            styles["af-add-policy-modal"]
                        )}>
                        <div className={styles["af-warning__header"]}>
                            <h2>Add a new policy owner</h2>
                        </div>
                        <div
                            className={cx(
                                styles["af-warning__content"],
                                styles["af-dialog-form"]
                            )}>
                            <Formik
                                initialValues={initialValue}
                                validationSchema={validationSchema}
                                onSubmit={(values) => handleSubmitForm(values)}>
                                {({
                                    values,
                                    handleChange,
                                    setFieldValue,
                                    handleBlur,
                                    touched,
                                    errors,
                                    isValid,
                                    isSubmitting
                                }) => (
                                    <Form autoComplete="off" className={styles["fa-question-form"]}>
                                        <div className={styles["af-form-col"]}>
                                            <div
                                                className={cx(
                                                    styles["af-dropdown-field"],
                                                    touched.title && errors.title
                                                        ? styles["error"]
                                                        : ""
                                                )}>
                                                <Dropdown
                                                    className={styles["af-dropdown"]}
                                                    id="title"
                                                    name="title"
                                                    options={titleOptionList}
                                                    selectedOptionId={values.title}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    variant={"default"}
                                                    title="Title"
                                                />
                                                {touched.title && errors.title && (
                                                    <ValidationMessage>
                                                        {errors.title}
                                                    </ValidationMessage>
                                                )}
                                            </div>

                                            <Input
                                                className={cx("gap-ml")}
                                                id="First name"
                                                name="firstName"
                                                type="text"
                                                value={values.firstName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                label="First name"
                                                placeholder="First name"
                                                hasError={
                                                    touched.firstName && errors.firstName
                                                }
                                            />
                                        </div>

                                        <div className={styles["af-form-col"]}>
                                            <Input
                                                className={cx("gap-ml")}
                                                id="Middle name"
                                                name="middleName"
                                                type="text"
                                                value={values.middleName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                label="Middle name"
                                                placeholder="Middle name"
                                                hasError={
                                                    touched.middleName &&
                                                    errors.middleName
                                                }
                                            />

                                            <Input
                                                className={cx("gap-ml")}
                                                id="Last name"
                                                name="lastName"
                                                type="text"
                                                value={values.lastName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                label="Last name"
                                                placeholder="Last name"
                                                hasError={
                                                    touched.lastName && errors.lastName
                                                }
                                            />
                                        </div>

                                        <div
                                            className={cx(
                                                styles["af-form-field"],
                                                "gap-ml"
                                            )}>
                                            <div
                                                className={cx([styles["af-form-label"]])}>
                                                Gender from birth
                                            </div>
                                            <fieldset
                                                className={styles["af-radio-button"]}
                                                id={`gender-group`}>
                                                <legend>Gender from birth</legend>
                                                <div className={styles["checklist-radio-button"]}>
                                                    <input
                                                        type="radio"
                                                        id="gender-male"
                                                        name="gender"
                                                        value={"Male"}
                                                        onChange={handleChange}
                                                    />
                                                    <label
                                                        className={
                                                            styles[
                                                                "radion-button--label-container"
                                                            ]
                                                        }
                                                        htmlFor="gender-male">
                                                        <p
                                                            className={
                                                                styles["rb--label"]
                                                            }>
                                                            Male
                                                        </p>
                                                    </label>
                                                </div>
                                                <div className={styles["checklist-radio-button"]}>
                                                    <input
                                                        type="radio"
                                                        id="gender-female"
                                                        name="gender"
                                                        value={"Female"}
                                                        onChange={handleChange}
                                                    />
                                                    <label
                                                        className={
                                                            styles[
                                                                "radion-button--label-container"
                                                            ]
                                                        }
                                                        htmlFor="gender-female">
                                                        <p
                                                            className={
                                                                styles["rb--label"]
                                                            }>
                                                            Female
                                                        </p>
                                                    </label>
                                                </div>
                                            </fieldset>
                                        </div>

                                        <div
                                            className={cx(
                                                styles["af-form-field"],
                                                "gap-ml"
                                            )}>
                                            <label
                                                className={cx(
                                                    styles["af-form-label"],
                                                    Boolean(isErrorDOB) ||
                                                        (touched.dateOfBirth &&
                                                            errors.dateOfBirth)
                                                        ? styles["error"]
                                                        : ""
                                                )}
                                                htmlFor="Date of birth">
                                                Date of birth
                                            </label>
                                            <div
                                                className={cx(
                                                    styles["field-date-input"],
                                                    Boolean(isErrorDOB) ||
                                                        (touched.dateOfBirth &&
                                                            errors.dateOfBirth)
                                                        ? styles["error"]
                                                        : ""
                                                )}>
                                                <TextField
                                                    id="Date of birth"
                                                    name="dateOfBirth"
                                                    type="date"
                                                    placeholder="Date of birth"
                                                    value={values.dateOfBirth}
                                                    className={styles["af-datepicker"]}
                                                    onChange={(e) => {
                                                        const date = e.target.value;
                                                        setIsErrorDOB(
                                                            date
                                                                ? validDOB(
                                                                      {
                                                                          id: 1,
                                                                          rangeStart: 16,
                                                                          rangeEnd: 75
                                                                      },
                                                                      date
                                                                  )
                                                                : ""
                                                        );
                                                        setFieldValue(
                                                            "dateOfBirth",
                                                            date
                                                        );
                                                    }}
                                                    onBlur={handleBlur}
                                                    InputLabelProps={{
                                                        shrink: true
                                                    }}
                                                />
                                                {(Boolean(isErrorDOB) ||
                                                    (touched.dateOfBirth &&
                                                        errors.dateOfBirth)) && (
                                                    <ValidationMessage>
                                                        {isErrorDOB
                                                            ? isErrorDOB
                                                            : "This field is required"}
                                                    </ValidationMessage>
                                                )}
                                            </div>
                                        </div>

                                        <div
                                            className={cx(
                                                "gap-ml",
                                                cx(styles["af-form-col"])
                                            )}>
                                            <Input
                                                id="Phone"
                                                name="mobile"
                                                type="text"
                                                value={values.mobile}
                                                onChange={(e) => {
                                                    setFieldValue(
                                                        "mobile",
                                                        e.target.value.replace(
                                                            /[^0-9\-()]/g,
                                                            ""
                                                        )
                                                    );
                                                }}
                                                onBlur={handleBlur}
                                                label="Phone"
                                                placeholder="Mobile"
                                                hasError={touched.mobile && errors.mobile}
                                            />

                                            <Input
                                                className={styles["af-phone-input"]}
                                                id="Home"
                                                name="home"
                                                type="text"
                                                value={values.home}
                                                onChange={(e) => {
                                                    setFieldValue(
                                                        "home",
                                                        e.target.value.replace(
                                                            /[^0-9\-()]/g,
                                                            ""
                                                        )
                                                    );
                                                }}
                                                onBlur={handleBlur}
                                                placeholder="Home"
                                                hasError={touched.home && errors.home}
                                            />
                                        </div>

                                        <div
                                            className={cx(
                                                styles["af-form-field"],
                                                "gap-ml"
                                            )}>
                                            <label
                                                className={cx([styles["af-form-label"]])}
                                                htmlFor="Home address">
                                                Home address
                                            </label>
                                            {showAddress && (
                                                <AFAddressField
                                                    question={{ id: 99 }}
                                                    answerChanges={handleAddress}
                                                    label="Home address"
                                                    apiKey={googlePlacesApiKey}
                                                />
                                            )}
                                        </div>

                                        <Checkbox
                                            id="postalAddress"
                                            name="postalAddress"
                                            onChange={(e) =>
                                                setIsSameWithPostalAddress(
                                                    e.target.checked
                                                )
                                            }
                                            checked={isSameWithPostalAddress}
                                            size="m"
                                            className={cx(
                                                "gap-ml",
                                                styles["af-policy-check"]
                                            )}
                                            label={
                                                <span
                                                    className={cx(
                                                        "space-pre-m",
                                                        styles[
                                                            "af-declaration-check-label"
                                                        ]
                                                    )}>
                                                    This is also my postal address
                                                </span>
                                            }
                                        />

                                        {!isSameWithPostalAddress && (
                                            <div
                                                className={cx(
                                                    styles["af-form-field"],
                                                    "gap-ml"
                                                )}>
                                                <label
                                                    className={cx([
                                                        styles["af-form-label"]
                                                    ])}
                                                    htmlFor="Postal address">
                                                    Postal address
                                                </label>
                                                {showAddress && (
                                                    <AFAddressField
                                                        question={{ id: 999 }}
                                                        answerChanges={
                                                            handlePostalAddress
                                                        }
                                                        label="Postal address"
                                                        apiKey={googlePlacesApiKey}
                                                    />
                                                )}
                                            </div>
                                        )}

                                        <Input
                                            className={cx("gap-xl")}
                                            id="Email address"
                                            name="email"
                                            type="email"
                                            value={values.email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            label="Email address"
                                            placeholder="Email address"
                                            hasError={touched.email && errors.email}
                                        />

                                        <Button
                                            className={cx("btn-primary")}
                                            width="fill"
                                            component="button"
                                            size="s"
                                            type="submit"
                                            isDisabled={
                                                !isValid ||
                                                !!isErrorDOB ||
                                                isSubmitting ||
                                                isLoading
                                            }
                                            isSubmitting={isSubmitting || isLoading}
                                            variant="calltoaction">
                                            <span>Save</span>
                                        </Button>

                                        <Button
                                            className={styles["af-warning__cancel"]}
                                            width="fill"
                                            component="p"
                                            size="s"
                                            onClick={() => onClose(false)}
                                            variant="default">
                                            <span>Cancel</span>
                                        </Button>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                );
            }}
        </LModal>
    );
};

export default AFAddPolicyOwnerDialog;
